// ────────────────────────────────────────────────────────────────────────────
// Driver jobs — the order's stops as sent to drivers' phones (myTms).
//
// A JOB is one send of a set of stops, with their checkpoints, to one driver,
// as a frozen copy. Its state (Sent / Accepted / Queried / …) belongs to that
// send only — it is NOT the order's status, which this never changes. One
// order can have several jobs: Driver A's collection, Driver B's delivery.
//
// Sending again to the same driver is an update they accept again; a stop
// live with another driver moves only with an explicit reassign. The driver's
// Query is a message thread on the job, answered here.
// ────────────────────────────────────────────────────────────────────────────
import { useAddressesByIds } from '@my/directory-api/hooks';
import type { DispatchProposal, OrderDispatch, TripDispatchStatusValue } from '@my/tms-api';
import {
  useDispatchActions,
  useDispatchMessages,
  useDispatchProposals,
  useOrderDispatches,
} from '@my/tms-api/hooks';
import { ConfirmDialog, ErrorBanner, Loading, cn, showToast } from '@my/ui';
import { AlertTriangle, ArrowLeft, Loader2, MessageSquare, Send, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { taskTypeLabel } from '@/features/orders/lib/taskTypeConfig';
import { PhonePreview } from '@/features/settings/trip-checkpoints/components/PhonePreview';

const STATE_STYLE: Record<TripDispatchStatusValue, string> = {
  Sent: 'bg-sky-50 text-sky-800 ring-sky-200',
  Accepted: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  Queried: 'bg-amber-50 text-amber-900 ring-amber-300',
  Completed: 'bg-slate-100 text-slate-700 ring-slate-200',
  Withdrawn: 'bg-slate-50 text-slate-500 ring-slate-200',
  Superseded: 'bg-slate-50 text-slate-500 ring-slate-200',
};

const STATE_LABEL: Record<TripDispatchStatusValue, string> = {
  Sent: 'Sent — not accepted yet',
  Accepted: 'Accepted',
  Queried: 'Queried',
  Completed: 'Completed',
  Withdrawn: 'Withdrawn',
  Superseded: 'Replaced by an update',
};

const LIVE: readonly TripDispatchStatusValue[] = ['Sent', 'Accepted', 'Queried'];

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Where the panel opens: the jobs, straight on sending (the Checkpoints panel's
 * shortcut), or on the conversations (the order's Messages button).
 */
export type DispatchPanelView = 'jobs' | 'send' | 'messages';

/** What the send view starts from — a fresh send, or an update of one job. */
interface SendStart {
  resourceId: string | null;
  stopIds: string[] | null;
}

export function DispatchPanel({
  open,
  onClose,
  organisationId,
  orderId,
  orderNumber,
  startView = 'jobs',
}: {
  open: boolean;
  onClose: () => void;
  organisationId: string | null;
  orderId: string | null;
  orderNumber: string | null;
  startView?: DispatchPanelView;
}) {
  const [sending, setSending] = useState<SendStart | null>(null);
  const jobsQuery = useOrderDispatches(open ? organisationId : null, open ? orderId : null);
  const hasJobs = (jobsQuery.data?.dispatches.length ?? 0) > 0;

  // Straight to sending when asked to, or when nothing has been sent yet — an
  // empty list is a step with nothing on it. Once per opening: after a send
  // the panel shows the job that was just sent.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!open) {
      autoOpened.current = false;
      return;
    }
    if (autoOpened.current || sending) return;
    if (startView === 'send' || (startView === 'jobs' && jobsQuery.isSuccess && !hasJobs)) {
      autoOpened.current = true;
      setSending({ resourceId: null, stopIds: null });
    }
  }, [open, startView, jobsQuery.isSuccess, hasJobs, sending]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Closing the panel goes back to the list next time.
  useEffect(() => {
    if (!open) setSending(null);
  }, [open]);

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-slate-900/20 transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Driver jobs"
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-[34rem] max-w-[96%] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {sending && hasJobs && (
              <button
                type="button"
                onClick={() => setSending(null)}
                aria-label="Back to the jobs"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-slate-900">
                {sending ? 'Send to driver' : startView === 'messages' ? 'Driver messages' : 'Driver jobs'}
                {orderNumber ? ` · ${orderNumber}` : ''}
              </h2>
              <p className="text-[11px] text-slate-500">
                {sending
                  ? 'Pick the driver and their stops — they get a copy on myDriver to accept.'
                  : 'What has been sent to drivers’ phones. The order’s own status is not changed by any of this.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {open && sending ? (
            <SendView
              organisationId={organisationId}
              orderId={orderId}
              start={sending}
              onSent={() => setSending(null)}
            />
          ) : (
            <JobsView
              // A fresh mount per opening, so the threads open as this opening asks.
              key={`${open}-${startView}`}
              organisationId={organisationId}
              orderId={orderId}
              query={jobsQuery}
              onSend={(start) => setSending(start)}
              focusMessages={startView === 'messages'}
            />
          )}
        </div>
      </aside>
    </>
  );
}

// ── The jobs ────────────────────────────────────────────────────────────────

function JobsView({
  organisationId,
  orderId,
  query,
  onSend,
  focusMessages = false,
}: {
  organisationId: string | null;
  orderId: string | null;
  query: ReturnType<typeof useOrderDispatches>;
  onSend: (start: SendStart) => void;
  /** Opened from the order's Messages button: every conversation open, unread first. */
  focusMessages?: boolean;
}) {
  const jobs = query.data?.dispatches ?? [];
  const byUnread = (list: OrderDispatch[]) =>
    focusMessages ? [...list].sort((a, b) => b.unreadMessages - a.unreadMessages) : list;
  const live = byUnread(jobs.filter((d) => LIVE.includes(d.status)));
  const past = byUnread(jobs.filter((d) => !LIVE.includes(d.status)));
  // A conversation can live on a job that has since been replaced or withdrawn.
  const [showPast, setShowPast] = useState(focusMessages && past.some((d) => d.messageCount > 0));

  return (
    <div className="space-y-3 p-4">
      <button
        type="button"
        onClick={() => onSend({ resourceId: null, stopIds: null })}
        className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#002E72] px-3 text-xs font-semibold text-white hover:bg-[#00224B]"
      >
        <Send className="h-3.5 w-3.5" /> Send to driver
      </button>

      {query.isPending && <Loading label="Loading jobs…" />}
      {query.error && <ErrorBanner title="Couldn't load the jobs" message={query.error.message} />}

      {query.isSuccess && live.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 px-3 py-4 text-center text-xs text-slate-500">
          Nothing has been sent for this order yet.
        </p>
      )}

      {live.map((job) => (
        <JobCard
          key={job.id}
          job={job}
          organisationId={organisationId}
          orderId={orderId}
          onUpdate={() => onSend({ resourceId: job.resourceId, stopIds: job.stops.map((s) => s.orderTaskId) })}
          openThread={focusMessages && job.messageCount > 0}
        />
      ))}

      {past.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowPast((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-800"
          >
            {showPast ? 'Hide' : 'Show'} earlier jobs ({past.length})
          </button>
          {showPast && (
            <div className="mt-2 space-y-2">
              {past.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  organisationId={organisationId}
                  orderId={orderId}
                  openThread={focusMessages && job.messageCount > 0}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function JobCard({
  job,
  organisationId,
  orderId,
  onUpdate,
  openThread = false,
}: {
  job: OrderDispatch;
  organisationId: string | null;
  orderId: string | null;
  onUpdate?: () => void;
  /** Start with the conversation open. */
  openThread?: boolean;
}) {
  const isLive = LIVE.includes(job.status);
  const actions = useDispatchActions(organisationId, orderId);
  const [threadOpen, setThreadOpen] = useState(openThread || job.status === 'Queried');
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  const withdraw = async () => {
    try {
      await actions.withdraw.mutateAsync(job.id);
      showToast({ type: 'success', message: `Withdrawn from ${job.recipientName}’s phone.` });
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Failed to withdraw it.' });
    } finally {
      setConfirmWithdraw(false);
    }
  };

  return (
    <section className={cn('rounded-lg border px-3 py-2.5', isLive ? 'border-slate-200' : 'border-slate-100 opacity-70')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{job.recipientName}</p>
          <p className="text-[10px] text-slate-400">
            {job.revision > 1 && `Update ${job.revision} · `}sent {when(job.sentUtc)} by {job.sentByName}
            {job.acceptedUtc && ` · accepted ${when(job.acceptedUtc)}`}
          </p>
        </div>
        <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1', STATE_STYLE[job.status])}>
          {STATE_LABEL[job.status]}
        </span>
      </div>

      <ul className="mt-1.5 flex flex-wrap gap-1">
        {job.stops.map((s) => (
          <li key={s.orderTaskId} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700">
            {s.sequence}. {taskTypeLabel(s.taskType)}
          </li>
        ))}
      </ul>

      {job.message && <p className="mt-1.5 border-l-2 border-slate-300 pl-2 text-[11px] text-slate-600">{job.message}</p>}

      {isLive && job.changedSinceSent && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-amber-50 px-2 py-1.5">
          <span className="flex items-center gap-1.5 text-[11px] text-amber-900">
            <AlertTriangle className="h-3.5 w-3.5" /> The order has changed since this was sent.
          </span>
          {onUpdate && (
            <button type="button" onClick={onUpdate} className="text-[11px] font-semibold text-amber-900 hover:underline">
              Send update
            </button>
          )}
        </div>
      )}

      <div className="mt-2 flex items-center gap-3 text-[11px]">
        <button
          type="button"
          onClick={() => setThreadOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Messages{job.messageCount ? ` (${job.messageCount})` : ''}
          {job.unreadMessages > 0 && (
            <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white">
              {job.unreadMessages} new
            </span>
          )}
        </button>
        {isLive && onUpdate && !job.changedSinceSent && (
          <button type="button" onClick={onUpdate} className="text-slate-500 hover:text-slate-900">
            Change stops…
          </button>
        )}
        {isLive && (
          <button
            type="button"
            onClick={() => setConfirmWithdraw(true)}
            disabled={actions.busy}
            className="ml-auto text-slate-400 hover:text-red-600"
          >
            Withdraw
          </button>
        )}
      </div>

      {threadOpen && (
        <Thread organisationId={organisationId} chainId={job.chainId} orderId={orderId} canWrite={isLive} />
      )}

      <ConfirmDialog
        open={confirmWithdraw}
        tone="danger"
        title="Withdraw this job?"
        message={`It comes off ${job.recipientName}’s phone. Anything they have already done stays on the order.`}
        confirmLabel="Withdraw"
        onConfirm={withdraw}
        onCancel={() => setConfirmWithdraw(false)}
        busy={actions.withdraw.isPending}
      />
    </section>
  );
}

export function Thread({
  organisationId,
  chainId,
  orderId,
  canWrite,
}: {
  organisationId: string | null;
  chainId: string;
  orderId: string | null;
  canWrite: boolean;
}) {
  const thread = useDispatchMessages(organisationId, chainId, orderId);
  const [text, setText] = useState('');

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    try {
      await thread.post.mutateAsync(t);
      setText('');
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Failed to send it.' });
    }
  };

  return (
    <div className="mt-2 space-y-2 rounded-md border border-slate-100 bg-slate-50 p-2">
      {thread.isPending && <Loading label="Loading messages…" />}
      {thread.data?.length === 0 && <p className="text-center text-[11px] text-slate-400">No messages yet.</p>}
      <ul className="space-y-1.5">
        {thread.data?.map((m) => {
          const mine = m.fromRole === 'Planner';
          return (
            <li key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs',
                  mine ? 'bg-[#002E72] text-white' : 'bg-white text-slate-800 ring-1 ring-slate-200',
                )}
              >
                <p className="whitespace-pre-wrap">{m.text}</p>
                <p className={cn('mt-0.5 text-[9px]', mine ? 'text-white/60' : 'text-slate-400')}>
                  {m.authorName} · {when(m.sentUtc)}
                  {mine && (m.readUtc ? ' · read' : ' · not read yet')}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      {canWrite && (
        <div className="flex items-end gap-1.5">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            rows={2}
            maxLength={2000}
            placeholder="Reply to the driver…"
            className="min-w-0 flex-1 resize-none rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-sky-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={send}
            disabled={!text.trim() || thread.post.isPending}
            aria-label="Send message"
            className="flex h-8 w-8 items-center justify-center rounded-md bg-[#002E72] text-white disabled:opacity-40"
          >
            {thread.post.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Sending ─────────────────────────────────────────────────────────────────

function SendView({
  organisationId,
  orderId,
  start,
  onSent,
}: {
  organisationId: string | null;
  orderId: string | null;
  start: SendStart;
  onSent: () => void;
}) {
  const query = useDispatchProposals(organisationId, orderId, true);
  const actions = useDispatchActions(organisationId, orderId);
  const data = query.data;

  const [resourceId, setResourceId] = useState<string | null>(start.resourceId);
  const [picked, setPicked] = useState<Set<string> | null>(start.stopIds ? new Set(start.stopIds) : null);
  const [message, setMessage] = useState('');
  const [confirmReassign, setConfirmReassign] = useState(false);

  // First receivable driver by default, with all their stops.
  const proposal: DispatchProposal | undefined = useMemo(() => {
    if (!data) return undefined;
    return (
      data.proposals.find((p) => p.resourceId === resourceId) ??
      data.proposals.find((p) => p.canReceive) ??
      data.proposals[0]
    );
  }, [data, resourceId]);
  const stopIds = picked ?? new Set(proposal?.stops.map((s) => s.orderTaskId) ?? []);

  const addressIds = useMemo(() => (data?.preview.tasks ?? []).map((t) => t.addressId).filter((x): x is string => !!x), [data]);
  // The server sends the names; this only fills a gap when it couldn't.
  const addressesQuery = useAddressesByIds(data && Object.keys(data.placeNames).length === 0 ? organisationId : null, addressIds);
  const places = useMemo(() => {
    const map = new Map<string, string>(Object.entries(data?.placeNames ?? {}));
    for (const a of addressesQuery.data ?? []) {
      if (!map.has(a.id)) map.set(a.id, [a.name?.trim(), a.city?.trim()].filter(Boolean).join(', ') || a.fullAddress);
    }
    return map;
  }, [data, addressesQuery.data]);
  const placeOf = (addressId: string | null) => (addressId ? places.get(addressId) : undefined);

  if (query.isPending) return <div className="p-4"><Loading label="Working out who gets what…" /></div>;
  if (query.error) return <div className="p-4"><ErrorBanner title="Couldn't prepare the send" message={query.error.message} /></div>;
  if (!data) return null;

  const pickDriver = (id: string) => {
    setResourceId(id);
    setPicked(null);
  };
  const toggleStop = (id: string) => {
    const next = new Set(stopIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  // Finished, subcontracted or unassigned stops are never sent — not even when
  // an update pre-ticks every stop the job had, one of them since left.
  const notSendable = new Set(data.notSent.map((n) => n.orderTaskId));
  const sendIds = [...stopIds].filter((id) => !notSendable.has(id));
  // Every stop is done (or cancelled): nothing is left to put on a phone.
  const allFinished =
    data.preview.tasks.length > 0 &&
    data.preview.tasks.every((t) =>
      data.notSent.some((n) => n.orderTaskId === t.orderTaskId && (n.reason.startsWith('Done') || n.reason === 'Cancelled')),
    );

  // On ANOTHER driver's job — this driver's own live job is just being updated.
  const elsewhere = (s: DispatchProposal['stops'][number]) =>
    !!s.liveWith && s.liveWith.dispatchId !== proposal?.liveDispatch?.id;
  const reassigning = proposal?.stops.filter((s) => stopIds.has(s.orderTaskId) && elsewhere(s)) ?? [];
  const isUpdate = !!proposal?.liveDispatch;
  const canSend = data.mobileEnabled && !!proposal?.canReceive && sendIds.length > 0 && !actions.busy;

  const send = async (reassign: boolean) => {
    if (!proposal) return;
    try {
      await actions.send.mutateAsync({
        resourceId: proposal.resourceId,
        orderTaskIds: sendIds,
        message: message.trim() || null,
        reassign,
      });
      showToast({
        type: 'success',
        message: isUpdate ? `Update sent to ${proposal.recipientName}.` : `Sent to ${proposal.recipientName}.`,
      });
      onSent();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Failed to send it.' });
    } finally {
      setConfirmReassign(false);
    }
  };

  return (
    <div className="space-y-3 p-4">
      {!data.mobileEnabled && (
        <ErrorBanner
          title="TMS Mobile is off"
          message="Jobs can't be sent to drivers until TMS Mobile is switched on for this organisation (Central Configuration → Modules)."
        />
      )}

      {data.proposals.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 px-3 py-4 text-center text-xs text-slate-500">
          {allFinished
            ? 'Every stop on this order is done — there is nothing left to send.'
            : 'No stop still to do has one of our drivers assigned. Assign a driver to a task first.'}
        </p>
      )}

      {/* Who */}
      {data.proposals.length > 0 && (
        <Block title="Driver">
          <div className="space-y-1.5">
            {data.proposals.map((p) => (
              <label
                key={p.resourceId}
                className={cn(
                  'flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-2',
                  proposal?.resourceId === p.resourceId ? 'border-sky-300 bg-sky-50/50' : 'border-slate-200',
                  !p.canReceive && 'cursor-not-allowed opacity-60',
                )}
              >
                <input
                  type="radio"
                  name="recipient"
                  className="mt-0.5"
                  checked={proposal?.resourceId === p.resourceId}
                  disabled={!p.canReceive}
                  onChange={() => pickDriver(p.resourceId)}
                />
                <span className="min-w-0">
                  <span className="block text-xs font-medium text-slate-800">
                    {p.recipientName}
                    {p.liveDispatch && <span className="font-normal text-slate-500"> · already has this job — this sends an update</span>}
                  </span>
                  <span className="block text-[10px] text-slate-500">
                    {p.canReceive
                      ? `Assigned to ${p.stops.length} stop${p.stops.length === 1 ? '' : 's'}`
                      : p.cannotReceiveReason}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </Block>
      )}

      {/* What */}
      {proposal && (
        <Block title="Stops">
          <ul className="space-y-1">
            {data.preview.tasks.map((t) => {
              const own = proposal.stops.find((s) => s.orderTaskId === t.orderTaskId);
              const notSent = data.notSent.find((n) => n.orderTaskId === t.orderTaskId);
              const checked = stopIds.has(t.orderTaskId) && !notSendable.has(t.orderTaskId);
              return (
                <li key={t.orderTaskId}>
                  <label className={cn('flex items-start gap-2 text-xs', notSent && 'opacity-50')}>
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      disabled={!!notSent}
                      onChange={() => toggleStop(t.orderTaskId)}
                    />
                    <span className="min-w-0">
                      <span className="text-slate-800">
                        {t.sequence}. {taskTypeLabel(t.taskType)} · {placeOf(t.addressId) ?? `Stop ${t.sequence}`}
                      </span>
                      <span className="block text-[10px] text-slate-500">
                        {notSent
                          ? notSent.reason
                          : own
                            ? elsewhere(own) && checked
                              ? `On ${own.liveWith!.recipientName}’s job — sending it here moves it`
                              : elsewhere(own)
                                ? `On ${own.liveWith!.recipientName}’s job`
                                : own.liveWith
                                  ? 'On this driver’s job already'
                                  : 'Assigned to this driver'
                            : t.who
                              ? `Assigned to ${t.who}`
                              : 'Another driver'}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </Block>
      )}

      {proposal && (
        <Block title="Message (optional)">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder={`Hi ${proposal.recipientName.split(' ')[0]} — collect from … by 09:00.`}
            className="w-full resize-none rounded border border-slate-300 px-2 py-1 text-xs focus:border-sky-400 focus:outline-none"
          />
        </Block>
      )}

      {proposal && (
        <Block title="What they'll see">
          <PhonePreview
            preview={data.preview}
            role="driver"
            orderLabel={data.preview.facts.orderNumber}
            names={{ customer: data.customerName || null, places }}
            stopIds={new Set(sendIds)}
            recipientName={proposal.recipientName}
            message={message}
          />
        </Block>
      )}

      {proposal && (
        <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-4 py-2">
          <button
            type="button"
            disabled={!canSend}
            onClick={() => (reassigning.length ? setConfirmReassign(true) : send(false))}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#002E72] px-3 text-xs font-semibold text-white hover:bg-[#00224B] disabled:opacity-40"
          >
            {actions.send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {isUpdate ? 'Send update' : `Send to ${proposal.recipientName.split(' ')[0]}`}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmReassign}
        title="Move stops to this driver?"
        message={
          reassigning.length
            ? `${reassigning
                .map((s) => `Stop ${s.sequence} is on ${s.liveWith!.recipientName}’s job`)
                .join('; ')}. Sending moves ${reassigning.length === 1 ? 'it' : 'them'} here — the other driver gets an update without ${reassigning.length === 1 ? 'it' : 'them'}.`
            : null
        }
        confirmLabel="Move and send"
        onConfirm={() => send(true)}
        onCancel={() => setConfirmReassign(false)}
        busy={actions.send.isPending}
      />
    </div>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      {children}
    </section>
  );
}
